import React, { useState, useCallback } from 'react'

import { Stack, Spinner, useToast } from '@pnld/components-web'

import {
  useDashboardCensusYears,
  useDashboardCensus,
} from '@/api/dashboard/queries'
import { DashboardCensusPayload } from '@/api/dashboard/types'
import { getErrorMessage } from '@/utils/api'

import { GeoMapFilter, GeoMapFilterValues } from '../parts'
import { CensusSingleYear, CensusMultiYear } from './parts'

const Census: React.FC = () => {
  const toast = useToast()
  const [filterValues, setFilterValues] = useState<GeoMapFilterValues | null>(
    null
  )

  const errorHandler = useCallback(
    (error: unknown) => {
      toast({
        status: 'error',
        title: getErrorMessage(error),
      })
    },
    [toast]
  )

  const mapGeoMapFilterToCensusPayload = useCallback(
    (values: GeoMapFilterValues): DashboardCensusPayload => {
      const years = (
        Array.isArray(values.censusYears)
          ? values.censusYears
          : values.censusYears
          ? [values.censusYears]
          : []
      ) as string[]

      return {
        nuCensusYear: years.map(year => Number(year)),
        coRegion: values.region || undefined,
        coUf: values.states.length ? values.states : undefined,
        coCity: values.cities.length ? values.cities : undefined,
        coEntity: values.schools.length ? values.schools : undefined,
        tpDependency: values.dependencies.length
          ? values.dependencies
          : undefined,
        tpLocation: values.locations.length ? values.locations : undefined,
        coSphere: values.spheres.length ? values.spheres : undefined,
        stPnldService: values.hasPndlService || undefined,
        coEducationStage: values.educationStages.length
          ? values.educationStages
          : undefined,
        coYearGrade: values.yearGrades.length ? values.yearGrades : undefined,
      }
    },
    []
  )

  const { data: censusYears, isLoading: isCensusYearsLoading } =
    useDashboardCensusYears({
      onError: errorHandler,
    })

  const { data: census, isLoading: isCensusLoadingRaw } = useDashboardCensus(
    filterValues
      ? mapGeoMapFilterToCensusPayload(filterValues)
      : ({} as DashboardCensusPayload),
    {
      onError: errorHandler,
      enabled: !!filterValues,
    }
  )

  const isCensusLoading = isCensusLoadingRaw && !!filterValues

  const censusYearOptions = (censusYears || []).map(year => ({
    label: String(year),
    value: String(year),
  }))

  const isMultiYear = filterValues
    ? Array.isArray(filterValues.censusYears) &&
      filterValues.censusYears.length > 1
    : false

  return (
    <Stack gap={4}>
      <GeoMapFilter
        censusYears={{
          value: censusYearOptions,
          isLoading: isCensusYearsLoading,
          isMulti: true,
        }}
        onChangeFilter={values => {
          if (
            values.censusYears &&
            ((Array.isArray(values.censusYears) &&
              values.censusYears.length > 0) ||
              (typeof values.censusYears === 'string' &&
                values.censusYears.trim() !== ''))
          ) {
            setFilterValues(values as GeoMapFilterValues)
          } else {
            setFilterValues(null)
          }
        }}
      />

      {isCensusLoading && (
        <Stack alignItems="center" justifyContent="center">
          <Spinner size="xl" />
        </Stack>
      )}

      {!isCensusLoading && census && !isMultiYear && (
        <CensusSingleYear census={census[0]} globalFilters={filterValues} />
      )}

      {!isCensusLoading && census && isMultiYear && (
        <CensusMultiYear census={census} globalFilters={filterValues} />
      )}
    </Stack>
  )
}

export default Census
